import { isPlatformBrowser } from "@angular/common";
import { Component, ElementRef, Inject, Input, OnDestroy, OnInit, PLATFORM_ID, signal, ViewChild } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";

/** Quiet period after the ad loads / its last height report before resizing the frame */
const AD_HEIGHT_SETTLE_MS = 200;

/** Show the frame anyway this long after it loads if no height report arrives */
const AD_REVEAL_FALLBACK_MS = 1500;

// ad-slot.component.ts
@Component({
  standalone: true,
  selector: 'app-ad-slot',
  templateUrl: './ad-slot.component.html'
})
export class AdSlotComponent implements OnInit, OnDestroy {
  @Input() slotUrl!: string;

  isBrowser = false;
  safeUrl!: SafeResourceUrl;
  iframeHeight = signal(90); // default height
  visible = signal(false);   // frame stays transparent until its height has settled

  private frameLoaded = false;
  private pendingHeight: number | null = null;
  private settleTimer?: ReturnType<typeof setTimeout>;
  private revealTimer?: ReturnType<typeof setTimeout>;

  @ViewChild('adFrame') adFrame?: ElementRef<HTMLIFrameElement>;

  constructor(
    private sanitizer: DomSanitizer,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  private messageHandler = (event: MessageEvent) => {
    // Only accept heights from this slot's own iframe — with several ad slots on a page,
    // every slot receives every ad's message
    if (
      event.origin === 'https://truckdriversus.com' &&
      event.source === this.adFrame?.nativeElement.contentWindow &&
      event.data?.type === 'adHeight'
    ) {
      // The ad page can report several heights while it loads (e.g. 0 before it has laid out, or
      // a tall one before its rotation hides the other ads). Resizing on each one makes the frame
      // collapse or jump tall over the page and then shrink. So hold reports until the ad has
      // fully loaded and they have stopped coming, then size the frame once and reveal it.
      const height = Number(event.data.height);
      if (!Number.isFinite(height) || height < 0) return;

      this.pendingHeight = height;
      if (this.frameLoaded) {
        this.scheduleApply();
      }
    }
  };

  onFrameLoad(event: Event) {
    // Ignore the frame's initial about:blank load (it fires while the view is still being
    // created, so use the event target rather than adFrame). The ad itself is cross-origin,
    // so reading its location throws.
    try {
      if ((event.target as HTMLIFrameElement).contentWindow?.location.href === 'about:blank') return;
    } catch {
      // cross-origin: this is the ad page loading
    }

    this.frameLoaded = true;
    this.scheduleApply();

    // Fallback reveal in case the ad page never reports a height
    clearTimeout(this.revealTimer);
    this.revealTimer = setTimeout(() => this.visible.set(true), AD_REVEAL_FALLBACK_MS);
  }

  private scheduleApply() {
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => this.applyHeight(), AD_HEIGHT_SETTLE_MS);
  }

  private applyHeight() {
    // Nothing reported yet: keep waiting (the fallback reveal covers a silent ad page)
    if (this.pendingHeight === null) return;

    this.iframeHeight.set(this.pendingHeight);
    clearTimeout(this.revealTimer);
    this.visible.set(true);
  }

  ngOnInit() {
    this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.slotUrl);
    if (this.isBrowser) {
      window.addEventListener('message', this.messageHandler);
    }
  }

  ngOnDestroy() {
    if (this.isBrowser) {
      window.removeEventListener('message', this.messageHandler);
    }
    clearTimeout(this.settleTimer);
    clearTimeout(this.revealTimer);
  }
}
